import { useEffect, useMemo, useState } from 'react';
import { API_URL, apiFetch, authHeaders, useAppStore } from '../../store';
import { useFedStore } from '../store';
import { useMlbb } from './store';
import NovaSchedule from './NovaSchedule';
import GroupBracketFlow from './GroupBracketFlow';
import { buildNovaBracket, dependentMatches, isDotaTournament } from './novaBracketEngine';

function MatchCard({ match, names, isAdmin, onEdit }) {
  return (
    <div className="rounded-xl border border-line bg-surface overflow-hidden" style={{ minWidth: 220 }}>
      <div className="text-fg-muted text-xs px-3 py-2 border-b border-line">{match.label}</div>
      {[['a', 'sa', match.aSource], ['b', 'sb', match.bSource]].map(([side, score, source]) => (
        <div key={side} className="flex items-center justify-between gap-3 px-3 py-2 border-b border-line" style={{ background: match.winner && match.winner === match[side] ? 'rgba(240,58,34,.15)' : undefined }}>
          <span className={match[side] ? 'text-fg font-semibold' : 'text-fg-faint'} style={{ fontSize: 13 }}>{names[match[side]] || source.label}</span>
          <span className="font-mono text-gold">{match[score] === '' ? '—' : match[score]}</span>
        </div>
      ))}
      {match.stale && <p className="text-gold text-xs px-3 py-2">Пара изменилась — нужен новый результат.</p>}
      {match.bye && <p className="text-fg-muted text-xs px-3 py-2">Автоматический проход · свободное место</p>}
      {isAdmin && <button type="button" onClick={() => onEdit(match)} disabled={!match.a || !match.b} className="fed-btn-ghost w-full py-2 text-xs disabled:opacity-40">Редактировать результат</button>}
    </div>
  );
}

export default function NovaBracket({ tour, poll = false }) {
  const lang = useFedStore((s) => s.lang);
  const kz = lang === 'kz';
  const game = isDotaTournament(tour) ? 'dota2' : 'cs2';
  const user = useAppStore((s) => s.user);
  const fire = useAppStore((s) => s.fire);
  const { results, tourId, startPolling, online, applyState } = useMlbb();
  const isAdmin = user?.user_type === 'admin' || user?.role === 'admin';
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ sa: '', sb: '', winner: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (poll) return startPolling(tour.id, game);
  }, [poll, tour.id, game, startPolling]);
  useEffect(() => { setEditing(null); }, [tour.id]);
  const built = useMemo(() => {
    try { return { bracket: buildNovaBracket(tour.groups, tourId === tour.id ? results : {}, { preliminary: tour.bracketPreliminary === true }) }; }
    catch (error) { return { error: error.message }; }
  }, [tour.groups, tour.id, tour.bracketPreliminary, tourId, results]);
  if (built.error) return <p role="alert" className="text-gold">{built.error}</p>;
  const bracket = built.bracket;
  const edit = (match) => {
    setEditing(match);
    setDraft({ sa: match.sa, sb: match.sb, winner: match.winner || '' });
  };
  const save = async (reset = false) => {
    if (busy || !editing) return;
    const current = bracket.matches.find((match) => match.id === editing.id);
    if (current.a !== editing.a || current.b !== editing.b) {
      fire('Пара изменилась. Откройте матч заново.', 'error');
      setEditing(null);
      return;
    }
    if (!reset && (!/^\d{1,3}$/.test(String(draft.sa)) || !/^\d{1,3}$/.test(String(draft.sb)) || ![current.a, current.b].includes(draft.winner))) {
      fire('Введите счёт обеих команд и выберите победителя.', 'error');
      return;
    }
    const changedOutcome = reset || current.winner !== draft.winner;
    const invalidate = changedOutcome ? dependentMatches(bracket.matches, current.id) : [];
    if (invalidate.some((id) => results[id]?.winner) && !window.confirm('Изменение победителя сбросит результаты зависимых матчей групп и плей-офф. Продолжить?')) return;
    setBusy(true);
    try {
      const response = await apiFetch(`${API_URL}/fed-teams/manual-result/${current.id}`, {
        method: 'PUT', headers: authHeaders(true),
        body: JSON.stringify({ tour: tour.id, game, a: current.a, b: current.b, ...draft, reset, invalidate }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Ошибка сервера');
      applyState(data);
      setEditing(null);
      fire(kz ? 'Нәтиже сақталды' : 'Результат сохранён', 'success');
    } catch (error) {
      fire(`Результат не сохранён: ${error.message}`, 'error');
    } finally { setBusy(false); }
  };
  const card = (match) => <MatchCard key={match.id} match={match} names={bracket.names} isAdmin={isAdmin} onEdit={edit} />;
  return (
    <div className="flex flex-col gap-5">
      {tour.bracketPreliminary && <p role="status" className="text-gold">Предварительная сетка · состав групп может измениться. Незанятые места ожидают команды.</p>}
      <div className="rounded-2xl border border-line p-5 bg-surface-2">
        <h3 className="font-display font-bold uppercase text-xl">{kz ? 'Топтық кезең' : 'Групповой этап'} · Double Elimination</h3>
        <p className="text-fg-muted text-sm mt-2">{kz ? 'Әр топтан екі команда плей-оффқа шығады. Торды көру үшін топты ашыңыз.' : 'Из каждой группы выходят две команды. Раскройте группу, чтобы посмотреть сетку.'}</p>
        {isAdmin && <p className="text-gold text-xs mt-2">Ручное редактирование доступно сразу. Результаты сохраняются отдельно от настроек турнира.</p>}
        {!online && <p role="status" className="text-gold text-xs mt-2">Нет связи с сервером. Сохранение результатов недоступно.</p>}
      </div>
      <div role="region" aria-label="Группы" className="grid grid-cols-1 md:grid-cols-2 items-start gap-4">
        {bracket.groups.map((group) => (
          <details key={group.id} className="rounded-2xl border border-line bg-surface-2 overflow-hidden md:col-span-2">
            <summary className="cursor-pointer p-5 text-fg font-display font-bold uppercase">
              {kz ? 'Топ' : 'Группа'} {group.id} <span className="text-fg-muted text-xs">· {group.teams.length} команд · раскрыть сетку</span>
            </summary>
            <div className="px-5 pb-4 text-sm text-fg-muted">{group.teams.join(' · ')}</div>
            <div className="px-5 pb-4 text-sm text-gold">1: {bracket.names[group.first] || '—'} · 2: {bracket.names[group.second] || '—'}</div>
            <div className="px-5 pb-5 flex flex-col gap-4">
              {group.teams.length === 5 && <p className="text-xs text-fg-muted">Предварительный матч: №3 против №4. Победитель играет с №5; проигравший переходит в нижнюю сетку.</p>}
              <GroupBracketFlow group={group} card={card} kz={kz} />
            </div>
          </details>
        ))}
      </div>
      <div className="rounded-2xl border border-line bg-surface-2 p-5">
        <h3 className="font-display font-bold uppercase text-xl mb-2">{kz ? 'Плей-офф' : 'Плей-офф'} · 8 команд</h3>
        <p className="text-fg-muted text-sm mb-4">Single Elimination · Победители групп играют со вторыми местами соседних групп.</p>
        <div className="flex gap-5 overflow-x-auto pb-3">{bracket.playoff.map((round, index) => <div key={index} className="flex flex-col justify-around gap-3" style={{ minWidth: 240 }}>{round.map(card)}</div>)}</div>
        {bracket.champion && <p className="text-gold font-bold mt-3">Победитель: {bracket.names[bracket.champion]}</p>}
      </div>
      <details className="rounded-2xl border border-line bg-surface-2 overflow-hidden">
        <summary className="cursor-pointer p-5 font-display font-bold uppercase text-xl">
          {kz ? '3-орын үшін матч' : 'Матч за 3-е место'}
        </summary>
        <div className="px-5 pb-5">
          <p className="text-fg-muted text-sm mb-4">{kz ? 'Жартылай финалда жеңілген командалар автоматты түрде осында өтеді.' : 'Проигравшие команды из полуфиналов автоматически попадают сюда.'}</p>
          <div className="max-w-md">{card(bracket.third)}</div>
          {bracket.third.winner && <p className="text-gold font-bold mt-3">{kz ? '3-орын' : '3-е место'}: {bracket.names[bracket.third.winner]}</p>}
        </div>
      </details>
      <NovaSchedule tour={tour} bracket={bracket} isAdmin={isAdmin} fire={fire} />
      {editing && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,.8)' }}>
        <form onSubmit={(event) => { event.preventDefault(); save(); }} role="dialog" aria-modal="true" aria-label="Редактирование результата" className="rounded-2xl border border-line bg-surface-2 p-5 w-full max-w-md">
          <h3 className="font-bold text-lg mb-4">{editing.label}</h3>
          {[['a', 'sa'], ['b', 'sb']].map(([side, score]) => <label key={side} className="flex items-center justify-between gap-3 mb-3">
            <span>{bracket.names[editing[side]]}</span>
            <input aria-label={`Счёт ${bracket.names[editing[side]]}`} className="fed-input w-20 text-center" type="number" min="0" max="999" required value={draft[score]} onChange={(event) => setDraft({ ...draft, [score]: event.target.value })} />
          </label>)}
          <label className="flex flex-col gap-2">Победитель / проход дальше
            <select className="fed-input" required value={draft.winner} onChange={(event) => setDraft({ ...draft, winner: event.target.value })}>
              <option value="">Выберите команду</option>
              {[editing.a, editing.b].map((team) => <option key={team} value={team}>{bracket.names[team]}</option>)}
            </select>
          </label>
          <div className="flex gap-3 flex-wrap mt-5">
            <button disabled={busy || !online} className="fed-btn-primary px-4 py-2" type="submit">{busy ? 'Сохранение…' : 'Сохранить'}</button>
            <button disabled={busy || !online} className="fed-btn-ghost px-3 py-2" type="button" onClick={() => save(true)}>Сбросить</button>
            <button disabled={busy} className="fed-btn-ghost px-3 py-2" type="button" onClick={() => setEditing(null)}>Закрыть</button>
          </div>
        </form>
      </div>}
    </div>
  );
}
